import { AlertCircle, BookOpen, Copy, Image, Link, LoaderCircle, Pencil, Plus, RotateCcw, RotateCw, Search, Trash2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { ChangeEvent, FormEvent, RefObject } from "react";
import { AppContainer } from "../components/AppContainer";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import type { SwitcherUser } from "../components/AppSwitcher";
import { realtimeFetch } from "../realtime/client";
import { useRealtime } from "../realtime/useRealtime";
import { searchRecipes } from "./search";
import { formatRecipeEditorText, normalizeRecipeIngredientText } from "./parser";

interface Nutrients {
  calories: number;
  protein: number;
  carbohydrates: number;
  fat: number;
}

export interface Recipe {
  id: number;
  name: string;
  servings: number;
  ingredients: string;
  instructions: string;
  sourceText: string;
  warning: string;
  draft: boolean;
  previewImage: { filename: string; width: number; height: number; thumbnail?: { filename: string; mimeType: string; width: number; height: number } | null } | null;
  nutrition: { whole?: Nutrients; perServing?: Nutrients } | null;
  nutritionStatus: string;
  parseStatus: string;
  parsed: Record<string, unknown> | null;
}

function fail(response: Response) {
  return response.json().catch(() => null).then(
    (value: { error?: { message?: string } } | null) =>
      value?.error?.message ?? "Something went wrong. Please try again.",
  );
}

function AutoGrowingTextarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const textarea = useRef<HTMLTextAreaElement>(null);
  const resize = (allowShrink = false) => {
    const element = textarea.current;
    if (!element) return;
    if (allowShrink) element.style.height = "auto";
    if (allowShrink || element.scrollHeight > element.clientHeight) element.style.height = `${element.scrollHeight}px`;
  };
  useEffect(() => { resize(document.activeElement !== textarea.current); }, [props.value]);
  return <textarea
    {...props}
    ref={textarea}
    className={`${props.className ?? ""} recipe-auto-textarea`.trim()}
    onInput={event => { resize(); props.onInput?.(event); }}
    onBlur={event => { resize(true); props.onBlur?.(event); }}
  />;
}

function Shell({ user, onSignOut, title = "Recipes", backHref, children, action, fullTitle = false }: {
  user: SwitcherUser;
  onSignOut: () => Promise<void>;
  title?: string;
  backHref?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  fullTitle?: boolean;
}) {
  return (
    <AppContainer
      className={`recipes-shell app-recipes${fullTitle ? " recipe-detail-shell" : ""}`}
      contentClassName="recipes-content"
      currentDestination="recipes"
      user={user}
      onSignOut={onSignOut}
      title={title}
      asHeading
      backHref={backHref}
      backLabel="Back to Recipes"
      actions={action}
    >
      {children}
    </AppContainer>
  );
}

function RecipeWorkingIndicator({ label }: { label: string }) {
  return <span className="recipe-working-indicator" role="status" aria-label={label}><LoaderCircle size={20} aria-hidden="true" /></span>;
}

function RecipeImage({ recipe, className = "", alt = "", thumbnail = false }: { recipe: Recipe; className?: string; alt?: string; thumbnail?: boolean }) {
  const imageKey = recipe.previewImage ? `${recipe.id}:${recipe.previewImage.filename}:${recipe.previewImage.thumbnail?.filename ?? ""}` : "";
  const [attempt, setAttempt] = useState(0);
  useEffect(() => { setAttempt(0); }, [imageKey]);
  if (!recipe.previewImage || attempt > 1) return <span className={`recipe-image-placeholder ${className}`} aria-hidden="true"><Image size={24} /></span>;
  const hasThumbnail = thumbnail && Boolean(recipe.previewImage.thumbnail);
  const useThumbnail = hasThumbnail && attempt === 0;
  const source = useThumbnail ? recipe.previewImage.thumbnail! : recipe.previewImage;
  const route = useThumbnail ? "thumbnail" : "image";
  const src = `/api/recipes/${recipe.id}/${route}?v=${encodeURIComponent(source.filename)}${attempt ? "&retry=1" : ""}`;
  return <img className={className} src={src} alt={alt} width={source.width} height={source.height} loading={thumbnail ? "lazy" : undefined} decoding={thumbnail ? "async" : undefined} onError={() => setAttempt(current => current + 1)} />;
}

function Nutrition({ recipe }: { recipe: Recipe }) {
  const values = recipe.nutrition;
  if (!values?.whole || !values.perServing) {
    return <p className="recipe-nutrition-state">{recipe.nutritionStatus === "pending" ? "Calculating nutrition…" : "Nutrition is not available."}</p>;
  }
  const items: Array<[keyof Nutrients, string]> = [
    ["calories", "Calories"],
    ["protein", "Protein"],
    ["carbohydrates", "Carbohydrates"],
    ["fat", "Fat"],
  ];
  return (
    <table className="recipe-nutrition">
      <thead><tr><th scope="col"><span className="sr-only">Nutrient</span></th><th scope="col">Per serving</th><th scope="col">Total</th></tr></thead>
      <tbody>{items.map(([key, label]) => <tr key={key}><th scope="row">{label}</th><td>{Math.round(values.perServing![key])}{key === "calories" ? " kcal" : " g"}</td><td>{Math.round(values.whole![key])}{key === "calories" ? " kcal" : " g"}</td></tr>)}</tbody>
    </table>
  );
}

export function instructionSteps(instructions: string) {
  return instructions
    .split(/\n+/)
    .map(step => step.trim().replace(/^(?:\d+[.)]|[-*])\s+/, ""))
    .filter(Boolean);
}

function RecipeIngredients({ recipe }: { recipe: Recipe }) {
  return <p className="recipe-text">{normalizeRecipeIngredientText(recipe.ingredients) || "No ingredients were provided."}</p>;
}

export function RecipeDetailContent({ recipe }: { recipe: Recipe }) {
  const steps = instructionSteps(recipe.instructions);
  return (
    <article className="recipe-detail">
      <RecipeImage recipe={recipe} className="recipe-hero" alt={recipe.previewImage ? recipe.name : ""} />
      {recipe.warning && <p className="recipe-warning"><AlertCircle size={18} />{recipe.warning}</p>}
      <section className="recipe-section">
        <h3>Ingredients</h3>
        <RecipeIngredients recipe={recipe} />
      </section>
      <section className="recipe-section">
        <h3>Instructions</h3>
        {steps.length ? <ol className="recipe-instructions">{steps.map((step, index) => <li key={`${index}-${step}`}><span className="recipe-step-number" aria-hidden="true">{index + 1}.</span><span>{step}</span></li>)}</ol> : <p className="recipe-text">No instructions were provided.</p>}
      </section>
      <section className="recipe-section">
        <h3>Estimated Nutrition</h3>
        <p className="recipe-servings">{recipe.servings === 1 ? "1 serving" : `${recipe.servings} servings`}</p>
        <Nutrition recipe={recipe} />
      </section>
    </article>
  );
}

function AddMenu() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const trigger = useRef<HTMLButtonElement>(null);
  const uploadInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [open]);
  const importImage = async (file: File) => {
    setBusy(true);
    setError("");
    try {
      const form = new FormData();
      form.set("image", file);
      const response = await realtimeFetch("/api/recipes/import-image", { method: "POST", body: form });
      if (!response.ok) throw new Error(await fail(response));
      const recipe = (await response.json() as { recipe: Recipe }).recipe;
      window.location.assign(`/recipes/${recipe.id}`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not import this recipe.");
      setBusy(false);
      setOpen(true);
    }
  };
  const chooseImage = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    event.target.value = "";
    if (selected) void importImage(selected);
  };
  const chooseSource = (input: RefObject<HTMLInputElement | null>) => {
    setOpen(false);
    input.current?.click();
  };
  return (
    <div className="recipe-add">
      <input ref={uploadInput} name="recipe-image-upload" autoComplete="off" className="recipe-image-file-input" type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={chooseImage} disabled={busy} />
      <button ref={trigger} className="recipe-add-button" type="button" aria-label={busy ? "Importing recipe" : "Add recipe"} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(value => !value)} disabled={busy}>{busy ? <RecipeWorkingIndicator label="Importing recipe" /> : <Plus size={24} />}</button>
      {open && <>
        <button className="recipe-menu-scrim" type="button" aria-label="Close add menu" onClick={() => setOpen(false)} />
        <div className="recipe-add-menu" role="menu">
          <a role="menuitem" href="/recipes/new"><Pencil size={19} aria-hidden="true" />Enter manually</a>
          <a role="menuitem" href="/recipes/import-url"><Link size={19} aria-hidden="true" />Import from URL</a>
          <button type="button" role="menuitem" onClick={() => chooseSource(uploadInput)} disabled={busy}><Image size={19} aria-hidden="true" />Import from photo</button>
          {error && <div className="inline-error" role="alert">{error}</div>}
        </div>
      </>}
    </div>
  );
}

export function RecipeListRow({ recipe }: { recipe: Recipe }) {
  return (
    <li>
      <a href={`/recipes/${recipe.id}`} className="recipe-row">
        <RecipeImage recipe={recipe} className="recipe-thumb" thumbnail />
        <span>{recipe.name}</span>
        {recipe.warning && <AlertCircle size={17} aria-label="Has a warning" />}
      </a>
    </li>
  );
}

function ListPage({ user, onSignOut }: { user: SwitcherUser; onSignOut: () => Promise<void> }) {
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const load = async () => {
    try {
      const response = await fetch("/api/recipes");
      if (!response.ok) throw new Error(await fail(response));
      setRecipes((await response.json() as { recipes: Recipe[] }).recipes);
      setError("");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load recipes.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, []);
  useRealtime("recipes", () => void load());
  const results = searchRecipes(recipes, query);
  return (
    <Shell user={user} onSignOut={onSignOut} action={<AddMenu />}>
      <div className="recipe-search">
        <Search size={19} aria-hidden="true" />
        <input name="recipe-search" type="search" autoComplete="off" autoCapitalize="none" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search recipes" aria-label="Search recipes" />
        {query && <button className="search-clear-button" type="button" onClick={() => setQuery("")} aria-label="Clear recipe search"><X size={17} aria-hidden="true" /></button>}
      </div>
      {error && <div className="inline-error" role="alert"><AlertCircle size={18} />{error}<button type="button" onClick={() => void load()}>Retry</button></div>}
      {loading
        ? <div className="recipe-state"><span className="loading-dot" />Loading recipes…</div>
        : !recipes.length
          ? <section className="recipe-empty"><h2>No recipes yet</h2><p>Save a family favorite whenever you’re ready.</p></section>
          : !results.length
            ? <section className="recipe-empty"><h2>No matches</h2><p>Try a different recipe name.</p></section>
            : <ul className="recipe-list">{results.map(recipe => <RecipeListRow key={recipe.id} recipe={recipe} />)}</ul>}
    </Shell>
  );
}

function Detail({ id, user, onSignOut }: { id: number; user: SwitcherUser; onSignOut: () => Promise<void> }) {
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [error, setError] = useState("");
  const load = () => void fetch(`/api/recipes/${id}`)
    .then(async response => { if (!response.ok) throw new Error(await fail(response)); return response.json() as Promise<{ recipe: Recipe }>; })
    .then(value => { setRecipe(value.recipe); setError(""); })
    .catch(caught => setError(caught instanceof Error ? caught.message : "Could not load recipe."));
  useEffect(load, [id]);
  useRealtime("recipes", load);
  if (error) return <Shell user={user} onSignOut={onSignOut} backHref="/recipes/"><div className="inline-error" role="alert">{error}</div></Shell>;
  if (!recipe) return <Shell user={user} onSignOut={onSignOut} backHref="/recipes/"><div className="recipe-state"><span className="loading-dot" />Loading recipe…</div></Shell>;
  return <Shell user={user} onSignOut={onSignOut} title={recipe.name} backHref="/recipes/" fullTitle><RecipeDetailActions recipe={recipe} /><RecipeDetailContent recipe={recipe} /></Shell>;
}

export function RecipeDetailActions({ recipe }: { recipe: Recipe }) {
  return <nav className="recipe-detail-toolbar" aria-label="Recipe actions">
    <a className="recipe-header-work" href={`/recipes/${recipe.id}/work`}><BookOpen size={19} aria-hidden="true" />Work</a>
    <RecipeCopyButton recipe={recipe} />
    <a className="recipe-header-edit" href={`/recipes/${recipe.id}/edit`} aria-label={`Edit ${recipe.name}`}><Pencil size={19} aria-hidden="true" />Edit</a>
  </nav>;
}

function goBackFromWork(id: number) {
  const referrer = typeof document === "undefined" ? "" : document.referrer;
  const detailPath = new RegExp(`^/recipes/${id}/?$`);
  if (typeof window !== "undefined" && referrer) {
    try {
      const previous = new URL(referrer, window.location.href);
      if (previous.origin === window.location.origin && detailPath.test(previous.pathname)) {
        window.history.back();
        return;
      }
    } catch {
      // Fall through to the safe list route for malformed referrers.
    }
  }
  if (typeof window !== "undefined") window.location.assign("/recipes/");
}

export function RecipeWorkContent({ recipe, user, onSignOut }: { recipe: Recipe; user: SwitcherUser; onSignOut: () => Promise<void> }) {
  const steps = instructionSteps(recipe.instructions);
  return (
    <AppContainer
      className="recipes-shell recipe-work-shell app-recipes"
      contentClassName="recipe-work-scroll"
      currentDestination="recipes"
      user={user}
      onSignOut={onSignOut}
      title={recipe.name}
      asHeading
      backLabel="Back to recipe"
      onBack={() => goBackFromWork(recipe.id)}
    >
      <div className="recipe-work-panels">
        <section className="recipe-work-panel recipe-work-ingredients" tabIndex={0} aria-labelledby="recipe-work-ingredients-heading">
          <h2 id="recipe-work-ingredients-heading">Ingredients</h2>
          <RecipeIngredients recipe={recipe} />
        </section>
        <section className="recipe-work-panel recipe-work-instructions" tabIndex={0} aria-labelledby="recipe-work-instructions-heading">
          <h2 id="recipe-work-instructions-heading">Instructions</h2>
          {steps.length
            ? <ol>{steps.map((step, index) => <li key={`${index}-${step}`}><span aria-hidden="true">{index + 1}.</span><span>{step}</span></li>)}</ol>
            : <p>No instructions were provided.</p>}
        </section>
      </div>
    </AppContainer>
  );
}

function RecipeWork({ id, user, onSignOut }: { id: number; user: SwitcherUser; onSignOut: () => Promise<void> }) {
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [error, setError] = useState("");
  const load = () => void fetch(`/api/recipes/${id}`)
    .then(async response => { if (!response.ok) throw new Error(await fail(response)); return response.json() as Promise<{ recipe: Recipe }>; })
    .then(value => { setRecipe(value.recipe); setError(""); })
    .catch(caught => setError(caught instanceof Error ? caught.message : "Could not load recipe."));
  useEffect(load, [id]);
  useRealtime("recipes", load);

  if (recipe) return <RecipeWorkContent recipe={recipe} user={user} onSignOut={onSignOut} />;
  return (
    <AppContainer className="recipes-shell recipe-work-shell recipe-work-state-shell app-recipes" currentDestination="recipes" user={user} onSignOut={onSignOut} title="Recipe Work mode" asHeading backLabel="Back to Recipes" onBack={() => goBackFromWork(id)}>
      {error
        ? <div className="inline-error" role="alert">{error}</div>
        : <div className="recipe-state"><span className="loading-dot" />Loading recipe…</div>}
    </AppContainer>
  );
}

function ImportPage({ user, onSignOut }: { user: SwitcherUser; onSignOut: () => Promise<void> }) {
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const response = await realtimeFetch("/api/recipes/import-url", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ url }) });
      if (!response.ok) throw new Error(await fail(response));
      const recipe = (await response.json() as { recipe: Recipe }).recipe;
      window.location.assign(`/recipes/${recipe.id}`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not import this recipe.");
      setBusy(false);
    }
  };
  return (
    <Shell user={user} onSignOut={onSignOut} title="Import from URL" backHref="/recipes/" action={busy ? <RecipeWorkingIndicator label="Importing recipe" /> : undefined}>
      <form className="recipe-form recipe-import-form" autoComplete="off" onSubmit={submit}>
        <label className="form-field"><span>URL</span><input autoFocus name="recipe-import-url" type="url" inputMode="url" autoComplete="off" value={url} onChange={event => setUrl(event.target.value)} placeholder="https://example.com/recipe" required disabled={busy} /></label>
        {error && <div className="inline-error" role="alert">{error}</div>}
        <button className="primary-button" type="submit" disabled={busy}>Import</button>
      </form>
    </Shell>
  );
}

export function recipeCopyText(recipe: Pick<Recipe, "name" | "servings" | "ingredients" | "instructions">) {
  return `${recipe.name}\n\n${formatRecipeEditorText(recipe)}`;
}

export async function copyRecipeText(text: string, navigatorValue: { clipboard?: { writeText(value: string): Promise<void> } } | undefined = typeof navigator === "undefined" ? undefined : navigator, documentValue: Document | undefined = typeof document === "undefined" ? undefined : document) {
  let clipboardError: unknown;
  if (navigatorValue?.clipboard) {
    try { await navigatorValue.clipboard.writeText(text); return; } catch (error) { clipboardError = error; }
  }
  if (!documentValue?.body || typeof documentValue.execCommand !== "function") throw clipboardError instanceof Error ? clipboardError : new Error("Copy is not supported.");
  const textarea = documentValue.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  documentValue.body.appendChild(textarea);
  try {
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    if (!documentValue.execCommand("copy")) throw clipboardError instanceof Error ? clipboardError : new Error("Copy failed.");
  } finally { textarea.remove(); }
}

function RecipeCopyButton({ recipe }: { recipe: Recipe }) {
  const [status, setStatus] = useState<"idle" | "copying" | "copied" | "failed">("idle");
  const reset = useRef<number | undefined>(undefined);
  useEffect(() => () => { if (reset.current !== undefined) window.clearTimeout(reset.current); }, []);
  const copy = async () => {
    setStatus("copying");
    try {
      await copyRecipeText(recipeCopyText(recipe));
      setStatus("copied");
      reset.current = window.setTimeout(() => setStatus("idle"), 1800);
    } catch { setStatus("failed"); }
  };
  return <span className="recipe-copy-action"><button className="recipe-header-copy" type="button" onClick={() => void copy()} disabled={status === "copying"}><Copy size={19} aria-hidden="true" />{status === "copied" ? "Copied" : "Copy"}</button>{status === "failed" && <span className="recipe-copy-error" role="alert">Could not copy recipe text.</span>}</span>;
}

export function DeleteRecipeDialog({ name, deleting, onCancel, onConfirm }: {
  name: string;
  deleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return <DeleteConfirmDialog title={<>Delete &quot;{name || "this recipe"}&quot;?</>} titleId="delete-recipe-title" description="This permanently deletes the recipe and its image. This cannot be undone." descriptionId="delete-recipe-copy" busy={deleting} onCancel={onCancel} onConfirm={onConfirm} />;
}

export function hasRecipeChanges(
  form: { name: string; sourceText: string; servings: string },
  baseline: { name: string; sourceText: string; servings: string },
  hasFile: boolean,
) {
  return hasFile || form.name !== baseline.name || form.sourceText !== baseline.sourceText || form.servings !== baseline.servings;
}

export function RecipeImageControls({ hasImage, canRotate = hasImage, busy, onRotate, onRemove, removeLabel }: {
  hasImage: boolean;
  canRotate?: boolean;
  busy: boolean;
  onRotate: (direction: "left" | "right") => void;
  onRemove: () => void;
  removeLabel: string;
}) {
  if (!hasImage) return null;
  return <>
    <button className="secondary-button recipe-remove-image" type="button" onClick={onRemove} disabled={busy}><Trash2 size={19} aria-hidden="true" />{removeLabel}</button>
    <span className="recipe-image-control-spacer" aria-hidden="true" />
    <button className="secondary-button recipe-rotate-image" type="button" onClick={() => onRotate("left")} disabled={busy || !canRotate} aria-label="Rotate image left" title="Rotate image left"><RotateCcw className="recipe-rotate-icon" size={19} aria-hidden="true" /></button>
    <button className="secondary-button recipe-rotate-image" type="button" onClick={() => onRotate("right")} disabled={busy || !canRotate} aria-label="Rotate image right" title="Rotate image right"><RotateCw className="recipe-rotate-icon" size={19} aria-hidden="true" /></button>
  </>;
}

export function RecipeImagePicker({ label, busy, onUpload, onUrl, defaultOpen = false }: {
  label: string;
  busy: boolean;
  onUpload: () => void;
  onUrl: () => void;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const trigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [open]);
  const choose = (action: () => void) => {
    setOpen(false);
    action();
  };
  return (
    <div className="recipe-image-picker">
      <button ref={trigger} className="recipe-file-control" type="button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(value => !value)} disabled={busy}><Image size={19} aria-hidden="true" /><span>{label}</span></button>
      {open && <>
        <button className="recipe-menu-scrim" type="button" aria-label="Close image menu" onClick={() => setOpen(false)} />
        <div className="recipe-image-menu" role="menu">
          <button type="button" role="menuitem" onClick={() => choose(onUpload)}><Image size={19} aria-hidden="true" />Upload</button>
          <button type="button" role="menuitem" onClick={() => choose(onUrl)}><Link size={19} aria-hidden="true" />URL</button>
        </div>
      </>}
    </div>
  );
}

export function RecipeImageUrlDialog({ initialValue, onCancel, onConfirm }: {
  initialValue: string;
  onCancel: () => void;
  onConfirm: (url: string) => void;
}) {
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    try {
      const url = new URL(value.trim());
      if (url.protocol !== "https:") throw new Error();
      onConfirm(url.href);
    } catch {
      setError("Enter a public HTTPS image URL.");
    }
  };
  return (
    <div className="recipe-dialog-scrim" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) onCancel(); }}>
      <form className="recipe-confirm-dialog recipe-image-url-dialog" autoComplete="off" role="dialog" aria-modal="true" aria-labelledby="recipe-image-url-title" onSubmit={submit}>
        <h2 id="recipe-image-url-title">Set image from URL</h2>
        <input name="recipe-image-url" type="url" inputMode="url" autoComplete="off" aria-label="Image URL" placeholder="https://example.com/image.jpg" value={value} onChange={event => { setValue(event.target.value); setError(""); }} autoFocus required />
        {error && <p className="inline-error" role="alert">{error}</p>}
        <div className="recipe-dialog-actions">
          <button className="secondary-button" type="button" onClick={onCancel}>Cancel</button>
          <button className="primary-button" type="submit">Use image</button>
        </div>
      </form>
    </div>
  );
}

export function UnsavedRecipeDialog({ saving, onCancel, onDiscard, onSave }: {
  saving: boolean;
  onCancel: () => void;
  onDiscard: () => void;
  onSave: () => void;
}) {
  return (
    <div className="recipe-dialog-scrim" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget && !saving) onCancel(); }}>
      <section className="recipe-confirm-dialog recipe-unsaved-dialog" role="dialog" aria-modal="true" aria-labelledby="unsaved-recipe-title" aria-describedby="unsaved-recipe-copy">
        <h2 id="unsaved-recipe-title">Save your changes?</h2>
        <p id="unsaved-recipe-copy">You have changes that have not been saved.</p>
        <div className="recipe-unsaved-actions">
          <button className="secondary-button" type="button" onClick={onCancel} disabled={saving}>Cancel</button>
          <button className="secondary-button recipe-discard-button" type="button" onClick={onDiscard} disabled={saving}>Revert</button>
          {saving && <RecipeWorkingIndicator label="Saving recipe" />}
          <button className="primary-button" type="button" onClick={onSave} disabled={saving}>Save</button>
        </div>
      </section>
    </div>
  );
}

async function rotatedImage(source: Blob, filename: string, direction: "left" | "right") {
  const url = URL.createObjectURL(source);
  try {
    const image = new window.Image();
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("The image could not be rotated."));
      image.src = url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = image.naturalHeight;
    canvas.height = image.naturalWidth;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("The image could not be rotated.");
    if (direction === "right") {
      context.translate(canvas.width, 0);
      context.rotate(Math.PI / 2);
    } else {
      context.translate(0, canvas.height);
      context.rotate(-Math.PI / 2);
    }
    context.drawImage(image, 0, 0);
    const type = /image\/(?:jpeg|png|webp)/.test(source.type) ? source.type : "image/jpeg";
    const output = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, type, .92));
    if (!output) throw new Error("The image could not be rotated.");
    return new File([output], filename, { type: output.type });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function Editor({ id, user, onSignOut }: { id?: number; user: SwitcherUser; onSignOut: () => Promise<void> }) {
  const storage = id ? `recipe-edit-${id}` : "recipe-manual-draft";
  const initial = { name: "", sourceText: "", servings: "1" };
  const [form, setForm] = useState(() => {
    if (id || typeof sessionStorage === "undefined") return initial;
    try {
      const saved = sessionStorage.getItem(storage);
      return saved ? { ...initial, ...(JSON.parse(saved) as Partial<typeof initial>) } : initial;
    } catch {
      return initial;
    }
  });
  const [baseline, setBaseline] = useState(initial);
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState("");
  const [removeImage, setRemoveImage] = useState(false);
  const [preview, setPreview] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showImageUrl, setShowImageUrl] = useState(false);
  const [leaveHref, setLeaveHref] = useState("");
  const imageInput = useRef<HTMLInputElement>(null);
  const createdRecipeId = useRef<number | null>(null);
  const allowNavigation = useRef(false);

  useEffect(() => {
    if (!id) return;
    void fetch(`/api/recipes/${id}`)
      .then(async response => { if (!response.ok) throw new Error(await fail(response)); return response.json() as Promise<{ recipe: Recipe }>; })
      .then(data => {
        const loaded = { name: data.recipe.name, sourceText: formatRecipeEditorText(data.recipe), servings: String(data.recipe.servings) };
        setRecipe(data.recipe);
        setForm(loaded);
        setBaseline(loaded);
      })
      .catch(caught => setError(caught instanceof Error ? caught.message : "Could not load recipe."));
  }, [id, storage]);
  useEffect(() => { sessionStorage.setItem(storage, JSON.stringify(form)); }, [form, storage]);
  useEffect(() => {
    const value = file ? URL.createObjectURL(file) : "";
    setPreview(value);
    return () => { if (value) URL.revokeObjectURL(value); };
  }, [file]);
  const dirty = hasRecipeChanges(form, baseline, Boolean(file) || Boolean(imageUrl.trim()) || removeImage);
  useEffect(() => {
    if (!dirty) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (allowNavigation.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    const linkNavigation = (event: MouseEvent) => {
      if (allowNavigation.current || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target instanceof Element ? event.target.closest("a[href]") as HTMLAnchorElement | null : null;
      if (!target || target.target === "_blank" || target.hasAttribute("download")) return;
      const destination = new URL(target.href, window.location.href);
      if (destination.href === window.location.href) return;
      event.preventDefault();
      setLeaveHref(destination.href);
    };
    window.addEventListener("beforeunload", beforeUnload);
    document.addEventListener("click", linkNavigation, true);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      document.removeEventListener("click", linkNavigation, true);
    };
  }, [dirty]);

  const field = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm(value => ({ ...value, [key]: event.target.value }));
  async function saveRecipe(destination?: string) {
    if (!form.name.trim()) {
      setError("A recipe name is required.");
      return;
    }
    if (!form.sourceText.trim()) {
      setError("Add recipe text before saving.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      let recipeId = id ?? createdRecipeId.current ?? undefined;
      if (!recipeId) {
        const draft = await realtimeFetch("/api/recipes/drafts", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name: form.name, sourceText: form.sourceText, sourceType: "manual" }) });
        if (!draft.ok) throw new Error(await fail(draft));
        recipeId = (await draft.json() as { recipe: Recipe }).recipe.id;
        createdRecipeId.current = recipeId;
      }
      const finalized = await realtimeFetch(`/api/recipes/${recipeId}/finalize`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name: form.name, sourceText: form.sourceText, servings: Number(form.servings) || 1 }) });
      if (!finalized.ok) throw new Error(await fail(finalized));
      if (file) {
        const upload = new FormData();
        upload.set("image", file);
        const imageResponse = await realtimeFetch(`/api/recipes/${recipeId}/image`, { method: "POST", body: upload });
        if (!imageResponse.ok) throw new Error(await fail(imageResponse));
      } else if (imageUrl.trim()) {
        const imageResponse = await realtimeFetch(`/api/recipes/${recipeId}/image`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ url: imageUrl.trim() }) });
        if (!imageResponse.ok) throw new Error(await fail(imageResponse));
      } else if (removeImage) {
        const imageResponse = await realtimeFetch(`/api/recipes/${recipeId}/image`, { method: "DELETE" });
        if (!imageResponse.ok) throw new Error(await fail(imageResponse));
      }
      sessionStorage.removeItem(storage);
      allowNavigation.current = true;
      window.location.assign(destination ?? `/recipes/${recipeId}`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save recipe.");
      setSaving(false);
      setLeaveHref("");
    }
  }
  async function save(event: FormEvent) {
    event.preventDefault();
    await saveRecipe();
  }
  function discardAndLeave() {
    if (!leaveHref) return;
    sessionStorage.removeItem(storage);
    allowNavigation.current = true;
    window.location.assign(leaveHref);
  }
  async function remove() {
    if (!id || deleting) return;
    setDeleting(true);
    setError("");
    try {
      const response = await realtimeFetch(`/api/recipes/${id}`, { method: "DELETE" });
      if (!response.ok) throw new Error(await fail(response));
      sessionStorage.removeItem(storage);
      allowNavigation.current = true;
      window.location.assign("/recipes/");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not delete recipe.");
      setDeleting(false);
      setConfirmDelete(false);
    }
  }
  async function rotateImage(direction: "left" | "right") {
    if ((!file && (!id || !recipe?.previewImage || removeImage)) || imageBusy) return;
    setImageBusy(true);
    setError("");
    try {
      let source: Blob;
      let filename: string;
      if (file) {
        source = file;
        filename = file.name;
      } else {
        const response = await fetch(`/api/recipes/${id}/image`);
        if (!response.ok) throw new Error(await fail(response));
        source = await response.blob();
        filename = `recipe-image.${source.type.split("/")[1] ?? "jpg"}`;
      }
      setFile(await rotatedImage(source, filename, direction));
      setRemoveImage(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The image could not be rotated.");
    } finally {
      setImageBusy(false);
    }
  }
  const busy = saving || deleting || imageBusy;
  const selectedPreview = preview || imageUrl.trim();
  const hasImage = Boolean(file || imageUrl.trim() || (recipe?.previewImage && !removeImage));
  return (
    <Shell user={user} onSignOut={onSignOut} title={id ? "Edit recipe" : "Add recipe"} backHref={id ? `/recipes/${id}` : "/recipes/"} action={<>{saving && <RecipeWorkingIndicator label="Saving recipe" />}<button className="primary-button recipe-header-save" type="submit" form="recipe-editor-form" disabled={busy}>Save</button></>}>
      <form id="recipe-editor-form" className="recipe-form recipe-editor" autoComplete="off" onSubmit={save}>
        {error && <div className="inline-error" role="alert">{error}</div>}
        <label className="form-field recipe-name-field"><span>Name</span><input autoFocus={!id} name="recipe-name" autoComplete="off" autoCapitalize="sentences" value={form.name} onChange={field("name")} maxLength={180} placeholder="Recipe name" disabled={busy} required /></label>
        <fieldset className="recipe-image-field">
          <legend>Image</legend>
          {selectedPreview ? <img className="recipe-editor-image" src={selectedPreview} alt="Selected finished dish" /> : recipe && !removeImage ? <RecipeImage recipe={recipe} className="recipe-editor-image" alt={recipe.previewImage ? recipe.name : ""} /> : <span className="recipe-image-placeholder recipe-editor-image" aria-hidden="true"><Image size={30} /></span>}
          <div className="recipe-image-controls">
            <RecipeImageControls hasImage={hasImage} canRotate={Boolean(file || (recipe?.previewImage && !removeImage && !imageUrl))} busy={busy} onRotate={direction => void rotateImage(direction)} onRemove={() => { if (file) setFile(null); else if (imageUrl) setImageUrl(""); else setRemoveImage(true); }} removeLabel="Remove" />
            <span className="recipe-image-control-spacer" aria-hidden="true" />
            <RecipeImagePicker label={hasImage ? "Replace" : "Set image"} busy={busy} onUpload={() => imageInput.current?.click()} onUrl={() => setShowImageUrl(true)} />
            <input ref={imageInput} name="recipe-image-file" autoComplete="off" className="recipe-image-file-input" type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={event => { setFile(event.target.files?.[0] ?? null); setImageUrl(""); setRemoveImage(false); event.target.value = ""; }} disabled={busy} />
          </div>
        </fieldset>
        <label className="form-field recipe-text-field"><span>Recipe</span><AutoGrowingTextarea name="recipe-source-text" autoComplete="off" autoCapitalize="sentences" rows={18} value={form.sourceText} onChange={field("sourceText")} maxLength={30000} required disabled={busy} /></label>
        <div className="recipe-form-actions">
          {id && <button className="recipe-delete" type="button" onClick={() => setConfirmDelete(true)} disabled={busy}><Trash2 size={18} />Delete</button>}
        </div>
      </form>
      {confirmDelete && <DeleteRecipeDialog name={form.name} deleting={deleting} onCancel={() => setConfirmDelete(false)} onConfirm={() => void remove()} />}
      {showImageUrl && <RecipeImageUrlDialog initialValue={imageUrl} onCancel={() => setShowImageUrl(false)} onConfirm={url => { setFile(null); setImageUrl(url); setRemoveImage(false); setShowImageUrl(false); }} />}
      {leaveHref && <UnsavedRecipeDialog saving={saving} onCancel={() => setLeaveHref("")} onDiscard={discardAndLeave} onSave={() => void saveRecipe(leaveHref)} />}
    </Shell>
  );
}

export function RecipesApp({ route, user, onSignOut }: { route: string; user: SwitcherUser; onSignOut: () => Promise<void> }) {
  if (route === "/recipes/new") return <Editor user={user} onSignOut={onSignOut} />;
  if (route === "/recipes/import-url") return <ImportPage user={user} onSignOut={onSignOut} />;
  const work = /^\/recipes\/(\d+)\/work$/.exec(route);
  if (work) return <RecipeWork id={Number(work[1])} user={user} onSignOut={onSignOut} />;
  const edit = /^\/recipes\/(\d+)\/edit$/.exec(route);
  if (edit) return <Editor id={Number(edit[1])} user={user} onSignOut={onSignOut} />;
  const detail = /^\/recipes\/(\d+)$/.exec(route);
  if (detail) return <Detail id={Number(detail[1])} user={user} onSignOut={onSignOut} />;
  return <ListPage user={user} onSignOut={onSignOut} />;
}
